import type { components } from '@tms/contracts';
import { get, post, put, request } from './client';

/**
 * Вызовы контура `web-*`.
 *
 * Тонкий слой поверх `client`: он существует, чтобы пути контракта были перечислены в
 * одном месте, а экраны обращались к ним по имени. Тип ответа берётся из
 * сгенерированных схем там, где схема есть.
 */

export type Order = components['schemas']['Order'];
export type OrderCreateRequest = components['schemas']['OrderCreateRequest'];
export type AssignmentCandidate = components['schemas']['AssignmentCandidate'];
export type LiveDriver = components['schemas']['LiveDriver'];
export type ActiveShift = components['schemas']['ActiveShift'];
export type IntegrationStatus = components['schemas']['IntegrationStatus'];
export type SloIndicator = components['schemas']['SloIndicator'];
export type PickupMatrix = components['schemas']['PickupMatrix'];
export type FeatureFlag = components['schemas']['FeatureFlag'];

/** Отделы единой инсталляции — режимы доставки (ADR-0030). */
export const DELIVERY_MODES = ['express', 'distribution'] as const;
export type DeliveryMode = (typeof DELIVERY_MODES)[number];

export interface Profile {
  readonly user_id: string;
  readonly full_name: string;
  readonly role: 'admin' | 'dispatcher';
  readonly installation_code: string;
  readonly sections: readonly string[];
  /** Отделы пользователя: данные других кабинет не получает вовсе (ADR-0030). */
  readonly modes: readonly DeliveryMode[];
  /** Соседние инсталляции, куда можно перейти из шапки (Р-015). */
  readonly peer_installations?: readonly string[];
}

export interface Page<T> {
  readonly data: T[];
  readonly next_cursor?: string | null;
  /** Сколько записей отвечает фильтру целиком. Есть не у всех списков контракта. */
  readonly total?: number;
}

export interface OrderFilters {
  readonly q?: string;
  readonly mode?: string;
  readonly status?: string;
  readonly zone_id?: string;
  readonly pickup_location_id?: string;
  readonly at_risk?: boolean;
  readonly attention?: boolean;
  readonly created_from?: string;
  readonly created_to?: string;
  readonly goods_location?: string;
  readonly cursor?: string;
  readonly limit?: number;
}

export const api = {
  // Отказ входа — это неверный пароль, а не истёкшая сессия: обновлять нечего.
  login: (phone: string, password: string): Promise<unknown> =>
    request('/web/auth/login', {
      method: 'POST',
      body: JSON.stringify({ phone, password }),
      retryOnUnauthorised: false,
    }),

  logout: (): Promise<void> => request<void>('/web/auth/logout', { method: 'POST' }),

  profile: (): Promise<Profile> => get<Profile>('/web/auth/me'),
  switchInstallation: (code: string): Promise<{ url: string }> =>
    post<{ url: string }>('/web/auth/switch-installation', { installation_code: code }),

  orders: (filters: OrderFilters): Promise<Page<Order>> =>
    get<Page<Order>>('/web/orders', { ...filters }),

  order: (id: string): Promise<Order> => get<Order>(`/web/orders/${id}`),

  /** Ручные координаты заказа (FR-505): ответ — заказ с пересчитанной зоной. */
  setOrderCoordinates: (id: string, lat: number, lng: number): Promise<Order> =>
    request<Order>(`/web/orders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ manual_lat: lat, manual_lng: lng }),
    }),

  orderAudit: (id: string): Promise<{ data: AuditEntry[] }> =>
    get<{ data: AuditEntry[] }>(`/web/orders/${id}/audit`),

  candidates: (id: string): Promise<CandidatesResult> =>
    get<CandidatesResult>(`/web/orders/${id}/candidates`),

  assign: (id: string, body: AssignRequest): Promise<unknown> =>
    post(`/web/orders/${id}/assign`, body),

  estimate: (body: EstimateRequest): Promise<EstimateResult> =>
    post<EstimateResult>('/web/orders/estimate', body),

  createOrder: (body: OrderCreateRequest): Promise<unknown> => post('/web/orders', body),

  cancelOrder: (id: string, reasonCode: string, comment?: string): Promise<unknown> =>
    post(`/web/orders/${id}/cancel`, { reason_code: reasonCode, comment }),

  returnToCt: (id: string, reasonCode: string, comment?: string): Promise<unknown> =>
    post(`/web/orders/${id}/return-to-ct`, { reason_code: reasonCode, comment }),

  resolve: (id: string, body: ResolutionRequest): Promise<unknown> =>
    post(`/web/orders/${id}/resolution`, body),

  resolutionQueue: (): Promise<{ data: ResolutionQueueItem[] }> =>
    get<{ data: ResolutionQueueItem[] }>('/web/resolutions/queue'),

  liveSnapshot: (
    filters: { zone_id?: string; pickup_location_id?: string } = {},
  ): Promise<{
    generated_at: string;
    drivers: LiveDriver[];
  }> => get('/web/live/snapshot', { ...filters }),

  activeShifts: (
    filters: { zone_id?: string; pickup_location_id?: string } = {},
  ): Promise<{
    generated_at: string;
    shifts: ActiveShift[];
  }> => get('/web/live/shifts', { ...filters }),

  driverTrack: (driverId: string, from: string, to: string): Promise<DriverTrack> =>
    get<DriverTrack>(`/web/drivers/${driverId}/track`, { from, to }),

  locations: (): Promise<Page<LocationView>> => get<Page<LocationView>>('/web/locations'),

  zones: (): Promise<ZoneView[]> => get<ZoneView[]>('/web/zones'),

  drivers: (): Promise<Page<DriverView>> => get<Page<DriverView>>('/web/drivers'),

  pickupMatrix: (): Promise<PickupMatrix> => get<PickupMatrix>('/web/pickup-matrix'),

  applyPickupMatrix: (operations: PickupOperation[]): Promise<{ applied: number }> =>
    post<{ applied: number }>('/web/pickup-matrix', { operations }),

  integrations: (): Promise<IntegrationStatus[]> =>
    get<IntegrationStatus[]>('/web/integrations/status'),

  retryEvent: (eventId: string): Promise<unknown> =>
    post(`/web/integrations/outbox/${eventId}/retry`),

  slo: (): Promise<SloIndicator[]> => get<SloIndicator[]>('/web/metrics/slo'),

  flags: (): Promise<FeatureFlag[]> => get<FeatureFlag[]>('/web/feature-flags'),

  setFlag: (code: string, enabled: boolean): Promise<unknown> =>
    put(`/web/feature-flags/${code}`, { is_enabled: enabled }),

  parameters: (): Promise<ParameterView[]> => get<ParameterView[]>('/web/parameters'),

  /** Без режима — общее значение; с режимом — значение отдела (ADR-0030). */
  setParameter: (key: string, value: unknown, mode?: DeliveryMode): Promise<ParameterView> =>
    put<ParameterView>(
      mode === undefined ? `/web/parameters/${key}` : `/web/parameters/${key}?mode=${mode}`,
      { value },
    ),

  users: (): Promise<{ data: UserView[] }> => get<{ data: UserView[] }>('/web/users'),

  setUserModes: (id: string, modes: readonly DeliveryMode[]): Promise<UserView> =>
    put<UserView>(`/web/users/${id}/modes`, { modes }),

  /** Рейсы смены: в `distribution` их несколько, и диспетчеру нужны все сразу. */
  shiftTasks: (shiftId: string): Promise<TaskView[]> =>
    get<TaskView[]>(`/web/shifts/${shiftId}/tasks`),

  task: (taskId: string): Promise<TaskView> => get<TaskView>(`/web/tasks/${taskId}`),

  reorderStops: (
    taskId: string,
    sequence: string[],
    options: { overrideCurrentLeg?: boolean; reason?: string } = {},
  ): Promise<TaskView> =>
    post<TaskView>(`/web/tasks/${taskId}/stops/reorder`, {
      sequence,
      override_current_leg: options.overrideCurrentLeg ?? false,
      reason: options.reason,
    }),

  reassignStop: (stopId: string, driverId: string, reason?: string): Promise<StopView> =>
    post<StopView>(`/web/stops/${stopId}/reassign`, { driver_id: driverId, reason }),

  replanShift: (shiftId: string): Promise<TaskView[]> =>
    post<TaskView[]>(`/web/shifts/${shiftId}/replan`),

  auditJournal: (filters: AuditFilters): Promise<Page<AuditEntry>> =>
    get<Page<AuditEntry>>('/web/audit', { ...filters }),

  auditFacets: (): Promise<AuditFacets> => get<AuditFacets>('/web/audit/facets'),

  /** Справочник причин: код остаётся кодом, человеку показывается `name` (CLAUDE.md 3.3). */
  reasonCodes: (kind: string): Promise<{ code: string; name: string }[]> =>
    get<{ code: string; name: string }[]>('/reference/reason-codes', { kind }),

  notificationTemplates: (): Promise<TemplateView[]> =>
    get<TemplateView[]>('/web/notification-templates'),

  saveTemplate: (body: TemplateInput): Promise<TemplateView> =>
    put<TemplateView>('/web/notification-templates', body),

  importCsv: (file: File, dryRun: boolean): Promise<ImportReport> => {
    const form = new FormData();
    form.set('file', file);
    form.set('dry_run', String(dryRun));

    // FormData отправляется без заголовка content-type: его выставляет браузер
    // вместе с границей блока, и подмена ломает разбор на сервере.
    return request<ImportReport>('/web/orders/import', { method: 'POST', body: form });
  },
};

export interface AuditEntry {
  readonly id: string;
  readonly entity?: string;
  readonly entity_id?: string | null;
  readonly action: string;
  readonly actor: {
    readonly kind: string | null;
    readonly id: string | null;
    readonly name?: string | null;
  };
  /** Объект по-человечески (Р-021): «A-000885», имя курьера; `order_id` — для перехода. */
  readonly object?: {
    readonly kind: string;
    readonly label: string | null;
    readonly order_id: string | null;
  };
  /** Имена объектов из значений: курьеры, зоны, точки. */
  readonly refs?: Readonly<Record<string, string>>;
  readonly category?: AuditCategory;
  /** Свёрнутых одинаковых событий подряд (1 — без повторов). */
  readonly repeat?: number;
  readonly old_value: unknown;
  readonly new_value: unknown;
  readonly created_at: string;
}

export const AUDIT_CATEGORIES = ['orders', 'routes', 'sessions', 'settings'] as const;

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

export interface AuditFilters {
  readonly category?: AuditCategory | undefined;
  /** Номер заказа: все события этого заказа. */
  readonly order?: string | undefined;
  readonly actor_id?: string | undefined;
  // `undefined` перечислен явно: в проекте включён exactOptionalPropertyTypes, и
  // «фильтр снят» выражается именно отсутствующим значением, а не пустой строкой.
  readonly entity?: string | undefined;
  readonly action?: string | undefined;
  readonly actor_role?: string | undefined;
  readonly entity_id?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly cursor?: string | undefined;
  readonly limit?: number | undefined;
}

export interface AuditFacets {
  readonly entities: string[];
  readonly actions: string[];
  readonly roles: string[];
  readonly actors?: { readonly id: string; readonly name: string; readonly role: string | null }[];
}

/** Точка задания в кабинете: то, что нужно для перестановки и разбора отклонений. */
export interface StopView {
  readonly id: string;
  readonly task_id: string;
  readonly order_id: string | null;
  readonly order_display_id: string | null;
  readonly location_name: string | null;
  readonly type: string;
  readonly sequence: number;
  readonly status: string;
  readonly is_locked: boolean;
  readonly is_pinned: boolean;
  readonly address: string | null;
  readonly eta_at: string | null;
  readonly planned_arrival_at: string | null;
  readonly actual_arrival_at: string | null;
  readonly planned_late_minutes: number | null;
  readonly planned_wait_minutes: number | null;
  readonly delivery_window_from: string | null;
  readonly delivery_window_to: string | null;
  readonly eta_deviation_minutes: number | null;
  readonly deviation_alert: boolean;
}

export interface TaskView {
  readonly id: string;
  readonly shift_id: string;
  readonly driver_id: string;
  readonly mode: string;
  readonly run_number: number;
  readonly status: string;
  readonly revision: number;
  readonly planned_distance_m: number | null;
  readonly planned_duration_minutes: number | null;
  readonly eta_complete_at: string | null;
  readonly eta_return_at: string | null;
  readonly stops: StopView[];
}

export interface TemplateView {
  readonly template_code: string;
  readonly language: string;
  readonly body: string;
  readonly is_enabled: boolean;
  readonly segments?: number;
  readonly length?: number;
}

export interface TemplateInput {
  readonly template_code: string;
  readonly language: string;
  readonly body: string;
  readonly is_enabled?: boolean;
}

export interface CandidatesResult {
  readonly candidates: AssignmentCandidate[];
  readonly excluded: {
    readonly driver_id: string;
    readonly driver_name?: string;
    readonly reason_code: string;
  }[];
}

export interface AssignRequest {
  readonly driver_id: string;
  readonly force?: boolean;
  readonly force_reason?: string;
}

export interface EstimateRequest {
  readonly mode: 'express' | 'distribution';
  readonly delivery_address: { readonly raw: string; readonly floor?: number | null };
  readonly pickup_location_id?: string;
  readonly desired_deadline_at?: string;
}

export interface EstimateResult {
  readonly feasible: boolean;
  readonly earliest_achievable_at: string | null;
  readonly eta_delivery_at: string | null;
  readonly available_couriers: number;
  readonly reason_code: string | null;
}

export interface ResolutionRequest {
  readonly decision: 'cancelled' | 'rescheduled' | 'reassigned';
  readonly new_deadline_at?: string;
  readonly new_driver_id?: string;
  readonly reason?: string;
}

export interface ResolutionQueueItem {
  readonly order: {
    readonly id: string;
    readonly display_id: string;
    readonly external_number: string | null;
    readonly address: { readonly raw: string };
    readonly recipient_name: string | null;
    readonly recipient_phone: string;
    readonly deadline_at: string | null;
    readonly goods_location: string;
    readonly status: string;
  };
  readonly attempt: {
    readonly reason_code: string | null;
    readonly comment: string | null;
    readonly occurred_at: string | null;
    readonly driver_id: string | null;
  } | null;
  readonly waiting_minutes: number;
  readonly ct_messages_count: number;
  readonly existing_resolution?: {
    readonly source: string;
    readonly resolved_at: string | null;
  };
}

export interface DriverTrack {
  readonly points: { readonly lat: number; readonly lng: number; readonly recorded_at: string }[];
  readonly distance_m: number;
  readonly mocked_count: number;
}

export interface LocationView {
  readonly id: string;
  readonly external_code: string;
  readonly name: string;
  readonly type: string;
  readonly is_active: boolean;
  readonly address?: { readonly raw: string } | null;
}

export interface ZoneView {
  readonly id: string;
  readonly name: string;
  readonly mode?: string;
  readonly priority?: number;
}

export interface DriverView {
  readonly id: string;
  readonly external_code: string | null;
  readonly full_name: string;
  readonly is_active: boolean;
  readonly modes?: readonly DeliveryMode[];
}

export interface UserView {
  readonly id: string;
  readonly phone: string;
  readonly full_name: string;
  readonly role: 'admin' | 'dispatcher' | 'driver';
  readonly modes: readonly DeliveryMode[];
  readonly is_active: boolean;
}

export interface PickupOperation {
  readonly driver_id: string;
  readonly location_id: string;
  readonly action: 'attach' | 'detach';
}

export interface ParameterView {
  readonly key: string;
  readonly value: unknown;
  readonly purpose: string;
  readonly unit?: string | null;
  readonly min?: number | null;
  readonly max?: number | null;
  /** Значения отделов, заданные отдельно от общего (ADR-0030). */
  readonly by_mode?: Partial<
    Record<DeliveryMode, { readonly value: unknown; readonly source: 'profile' | 'override' }>
  >;
}

export interface ImportReport {
  readonly total: number;
  readonly accepted: number;
  readonly rejected: number;
  readonly dry_run: boolean;
  readonly errors: { readonly line: number; readonly field: string; readonly code: string }[];
}
